import type CMSConnector from "./connector";
import type { NewsletterConfig } from "./rest/types";

interface NewsletterSignupParams {
  email: string;
  name: string;
  country: string;
  consents?: { id: number; accepted: boolean }[];
}

const messages = {
  de: {
    success:
      "Deine Anmeldung zum Newsletter war erfolgreich. Ab sofort erhältst du News & Updates direkt in dein Postfach.",
    error:
      "Leider hat etwas bei der Registrierung zum Newsletter nicht geklappt. Bitte versuche es noch einmal.",
  },
  en: {
    success:
      "Your newsletter subscription was successful. You will now receive news & updates directly in your mailbox.",
    error:
      "Sorry, something went wrong when registering for the newsletter. Please try to subscribe again.",
  },
};

export default class Newsletter {
  cms: CMSConnector;

  constructor(cms: CMSConnector) {
    this.cms = cms;
  }

  get enabled(): boolean {
    if (this.cms.campaign.newsletters === undefined) {
      console.warn(
        "cms.campaign.newsletters is undefined, may need backend update. Falling back to single newsletter."
      );
      return false;
    }
    for (const ns of this.cms.campaign.newsletters) {
      if (ns.enabled) return true;
    }
    return false;
  }

  allEnabledNewsletters(): NewsletterConfig[] {
    if (this.cms.campaign.newsletters === undefined) {
      console.warn(
        "cms.campaign.newsletters is undefined, may need backend update. Falling back to single newsletter."
      );
      return this.cms.campaign.newsletter.enabled
        ? [this.cms.campaign.newsletter]
        : [];
    }
    return this.cms.campaign.newsletters.filter((ns) => ns.enabled);
  }

  async signup(params: NewsletterSignupParams): Promise<void> {
    if (!this.enabled) {
      console.warn("CMS newsletter disabled");
      return;
    }
    const campaignId = this.cms.campaign.id;
    if (!campaignId) {
      console.warn("Campaign ID is not set, request not sent");
      return;
    }

    const locale = this.cms.i18n.currentLocale.code;

    const allEnabledNewsletters = this.allEnabledNewsletters();
    const acceptedIds = (params.consents || [])
      .filter((c) => c.accepted)
      .map((c) => c.id);
    const acceptedNewsletters: NewsletterConfig[] = [];
    for (const ns of allEnabledNewsletters) {
      if (ns.signup_mode == "implicit") {
        acceptedNewsletters.push(ns);
      } else if (acceptedIds.includes(ns.id)) {
        acceptedNewsletters.push(ns);
      } else if (ns.signup_mode == "required") {
        throw new Error(
          `Required consent for the newsletter with id ${ns.id} not provided`
        );
      }
    }

    const trackingParams = this.cms.tracking.urlParams;
    const successParams = new URLSearchParams({
      newsletter_state: "success",
      locale: locale,
      ...trackingParams,
    });

    for (const ns of acceptedNewsletters) {
      if (!ns.locales[locale]) {
        console.warn(
          `Newsletter with id ${ns.id} not set for locale '${locale}. Request not sent`
        );
        continue;
      }
      successParams.append(
        "address_book_id",
        ns.locales[locale].addressbook_id
      );
      successParams.append("form_id", ns.locales[locale].form_id);
    }

    const currentParams = new URLSearchParams(window.location.search);
    for (const [key, value] of Array.from(currentParams.entries())) {
      if (successParams.has(key)) {
        continue;
      }
      successParams.set(key, value);
    }
    const errorParams = new URLSearchParams(successParams);
    errorParams.set("newsletter_state", "error");

    const successUrl = new URL(window.location.href);
    successUrl.search = successParams.toString();
    const errorUrl = new URL(window.location.href);
    errorUrl.search = errorParams.toString();
    const hostUrl = new URL(window.location.href);
    hostUrl.search = "";
    hostUrl.hash = "";

    try {
      await this.cms.api.newsletter.signup(campaignId, {
        ...params,
        locale: locale,
        redirect_url: successUrl.toString(),
        error_redirect_url: errorUrl.toString(),
        host_url: hostUrl.toString(),
        newsletter_ids: acceptedNewsletters.map((ns) => ns.id),
      });
      this.cms.tracking.trackCustomer("newsletter_soi_success", {
        address_book_id: acceptedNewsletters[0].locales[locale].addressbook_id,
        form_id: acceptedNewsletters[0].locales[locale].form_id,
        email: params.email,
      });
    } catch (e) {
      this.cms.tracking.trackCustomer("newsletter_soi_fail", {
        address_book_id: acceptedNewsletters[0].locales[locale].addressbook_id,
        form_id: acceptedNewsletters[0].locales[locale].form_id,
        error_message: (e as Error).message,
        email: params.email,
      });
      throw e;
    }
  }

  get addressbookId(): string {
    // TODO: Check if any apps use this and whether it's actually needed
    const locale = this.cms.i18n.currentLocale.code;
    return this.cms.campaign.newsletter.locales[locale]?.addressbook_id || "";
  }

  get formId(): string {
    // TODO: Check if any apps use this and whether it's actually needed
    const locale = this.cms.i18n.currentLocale.code;
    return this.cms.campaign.newsletter.locales[locale]?.form_id || "";
  }

  initBanner(): void {
    if (sessionStorage.getItem("umg_newsletter_accepted") === "true") {
      return;
    }

    const urlSearchParams = new URLSearchParams(
      window.location.search ||
        window.location.hash.split("?")[1] ||
        window.location.hash.split("?")[0].split("#")[1] ||
        window.location.hash.split("?")[0].split("#")[0]
    );

    const state = urlSearchParams.get("newsletter_state");
    const locale = urlSearchParams.get("locale") || "de";
    const addressBookId =
      urlSearchParams.get("address_book_id") ||
      urlSearchParams.get("newsletter_selection"); // legacy, newsletter selection param is deprecated
    const formId: string | null = urlSearchParams.get("form_id"); // legacy, form ID always set in future

    if (!state || !locale || !addressBookId) return;

    // TODO: Messages should be configurable
    // TODO: Should follow app's default locale if i18n is set up
    let message = locale === "de" ? messages.de.success : messages.en.success;

    if (state === "success") {
      this.cms.tracking.trackCustomer("newsletter_doi_success", {
        address_book_id: addressBookId,
        form_id: formId || "",
      });
    } else if (state === "error") {
      message = locale === "de" ? messages.de.error : messages.en.error;

      this.cms.tracking.trackCustomer("newsletter_doi_fail", {
        address_book_id: addressBookId,
        form_id: formId || "",
        error_message: message,
      });
    }

    const $banner = document.createElement("div");
    // set data attribute for testing
    $banner.setAttribute("data-testid", "newsletter-banner");
    $banner.style.position = "fixed";
    $banner.style.top = "0";
    $banner.style.left = "0";
    $banner.style.right = "0";
    $banner.style.padding = "24px";
    $banner.style.background = "rgb(233, 77, 38)";
    $banner.style.color = "#fff";
    $banner.style.zIndex = "99";
    $banner.style.fontSize = "16px";
    $banner.style.fontFamily =
      "system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif,Apple Color Emoji,Segoe UI Emoji";
    $banner.textContent = message;

    const $close = document.createElement("div");
    $close.innerHTML =
      '<?xml version="1.0" encoding="iso-8859-1"?> <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 512 512" style="enable-background:new 0 0 512 512;" xml:space="preserve"> <g> <g> <polygon points="512,59.076 452.922,0 256,196.922 59.076,0 0,59.076 196.922,256 0,452.922 59.076,512 256,315.076 452.922,512 512,452.922 315.076,256 		"/> </g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> </svg>';
    $close.style.width = "16px";
    $close.style.fill = "white";
    $close.style.position = "absolute";
    $close.style.top = "28px";
    $close.style.right = "28px";
    $close.style.cursor = "pointer";

    $close.onclick = () => {
      $banner.remove();
    };

    $banner.appendChild($close);
    document.body.appendChild($banner);

    sessionStorage.setItem("umg_newsletter_accepted", "true");
  }
}
